import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import gtmTracking from '@app/utils/gtmTracking';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { Popup } from 'clad-ui';
import { config } from '@app/config';
import dynamic from 'next/dynamic';
import {
  createJsDatabase,
  getJsForm,
  updateCv,
  updateWorkStatus,
  updateIsShowInfo,
  createDecisionUpgrade,
} from './action';
import { DENY_UPGRADE_TEXT, ENTRYPOINT_MESSAGE, ENTRYPOINT_TYPE } from './constant';

const JsDatabaseModal = dynamic(() => import('./JsDatabaseModal'), {
  loading: () => <div />,
  ssr: false,
});

const JsDatabaseEntryPoint = dynamic(() => import('./JsDatabaseEntryPoint'), {
  loading: () => <div />,
  ssr: false,
});

const SnackBar = dynamic(() => import('../SnackBar/SnackBar'), {
  loading: () => <div />,
  ssr: false,
});

const JsDatabase = (props) => {
  const { isMobile, auth, dispatch } = props;
  const jsdbData = useSelector((state) => state.jsDatabase);
  const [showModal, setShowModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [modalType, setModalType] = useState('');
  const [isUpdateCv, setIsUpdateCv] = useState(false);
  const [formValue, setFormValue] = useState({});
  const [showDenyUpgrade, setShowDenyUpgrade] = useState(false);
  const { jsInfo, jsHaveCv, jsForm, loadedJsInfo, upgradeJsInfo } = jsdbData;

  useEffect(() => {
    if (loadedJsInfo) {
      let type = jsHaveCv && jsInfo?.remain_date > 0 ? ENTRYPOINT_TYPE.CV : ENTRYPOINT_TYPE.NON_CV;
      if (auth?.authenticated) {
        type = jsHaveCv ? ENTRYPOINT_TYPE.CV : ENTRYPOINT_TYPE.NON_CV;
      }
      setModalType(type);
    }
  }, [loadedJsInfo]);

  useEffect(() => {
    if (
      upgradeJsInfo?.loadedUpgradeInfo &&
      upgradeJsInfo?.is_show_info &&
      !isEmpty(upgradeJsInfo?.upgrade_info)
    ) {
      setFormValue({ ...formValue, ...upgradeJsInfo?.upgrade_info });
      setShowModal(upgradeJsInfo?.is_show_info);
    }
  }, [upgradeJsInfo?.loadedUpgradeInfo, upgradeJsInfo?.is_show_info, upgradeJsInfo?.upgrade_info]);

  useEffect(() => {
    const data = jsHaveCv ? jsInfo : jsForm;
    setFormValue(data);
  }, [jsForm, jsInfo]);

  const handleClickCreateCv = async () => {
    try {
      if (auth?.authenticated) {
        await dispatch(getJsForm());
      }
      setShowModal(!showModal);
      gtmTracking('job_seeker_database', 'from_job_homepage', 'click_entry_point', {
        account_id: auth?.account_id || 'none',
        finger_print: Cookies.get('ctfp') || 'none',
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmit = async (formInput) => {
    try {
      if (isUpdateCv) {
        await dispatch(updateCv(formInput));
        setFormValue({ ...formValue, ...formInput });
        // setIsUpdateCv(!isUpdateCv);
      } else {
        await dispatch(createJsDatabase(formInput));
      }
      setShowModal(false);
      setModalType(ENTRYPOINT_TYPE.CV);
      setShowSuccess(true);
      gtmTracking('job_seeker_database', 'from_job_homepage', 'submit_leave_info', {
        account_id: auth?.account_id || 'none',
        finger_print: Cookies.get('ctfp') || 'none',
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmitUpgradeInfo = async (formInput) => {
    if (!upgradeJsInfo?.next_action) {
      return;
    }
    try {
      if (upgradeJsInfo?.next_action === 'create') {
        await dispatch(createJsDatabase(formInput));
      } else {
        await dispatch(updateCv(formInput));
        setIsUpdateCv(true);
      }
      dispatch(updateIsShowInfo(false));
      setShowModal(false);
      setModalType(ENTRYPOINT_TYPE.CV);
      setShowSuccess(true);
      gtmTracking('job_seeker_database', 'from_job_homepage', 'submit_leave_info', {
        account_id: auth?.account_id || 'none',
        finger_print: Cookies.get('ctfp') || 'none',
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleOnClose = () => {
    setShowModal(!showModal);
    if (upgradeJsInfo?.is_show_info) {
      setShowDenyUpgrade(true);
    }
  };

  const handleToggleStatus = async (value) => {
    try {
      await dispatch(
        updateWorkStatus({
          is_open: value,
        })
      );
      if (value === true) {
        setShowSuccess(true);
      }
      const status = value === true ? 'open' : 'not_open';
      gtmTracking('job_seeker_database', `from_job_homepage | ${status}`, 'click_change_status', {
        account_id: auth?.account_id || 'none',
        finger_print: Cookies.get('ctfp') || 'none',
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleClickUpdate = async () => {
    try {
      setIsUpdateCv(true);
      gtmTracking('job_seeker_database', 'from_job_homepage', 'click_update_cv', {
        account_id: auth?.account_id || 'none',
        finger_print: Cookies.get('ctfp') || 'none',
      });
      setTimeout(() => {
        window.location.href = `${config.baseURL}/user/${auth?.account_oid}/chinh-sua-ho-so-xin-viec`;
      }, 200);
    } catch (err) {
      console.log(err);
    }
  };

  const handleUpgrade = async () => {
    try {
      await dispatch(
        createDecisionUpgrade({
          action: 'deny',
        })
      );
      setShowDenyUpgrade(false);
      dispatch(updateIsShowInfo(false));
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Fragment>
      <JsDatabaseEntryPoint
        type={modalType}
        onClickCreateCv={handleClickCreateCv}
        data={formValue}
        auth={auth}
        onClickToggle={handleToggleStatus}
        onClickUpdate={handleClickUpdate}
        isFoundCv={jsHaveCv}
      />
      {showModal && (
        <JsDatabaseModal
          auth={auth}
          isMobile={isMobile}
          open={showModal}
          onClose={handleOnClose}
          onSubmit={upgradeJsInfo?.is_show_info ? handleSubmitUpgradeInfo : handleSubmit}
          formInfo={formValue}
          dispatch={dispatch}
          isFoundCv={jsHaveCv}
          isShowInfo={upgradeJsInfo?.is_show_info}
        />
      )}

      {showSuccess && (
        <SnackBar
          hideCallBack={() =>
            setTimeout(() => {
              setShowSuccess(false);
              if (isUpdateCv) {
                setIsUpdateCv(false);
              }
            }, 2000)
          }
          message={
            isUpdateCv ? ENTRYPOINT_MESSAGE.UPDATE_CV_SUCCESS : ENTRYPOINT_MESSAGE.CREATE_CV_SUCCESS
          }
        />
      )}

      <Popup
        title="Huỷ cập nhật thông tin?"
        primaryBtnLabel="Không cập nhật thông tin"
        secondaryBtnLabel="Trở về"
        primaryColor="critical"
        btnGroupDirection="column"
        open={showDenyUpgrade}
        onClose={() => handleUpgrade()}
        onPrimaryButtonClick={() => handleUpgrade()}
        onSecondaryButtonClick={() => {
          setShowDenyUpgrade(!showDenyUpgrade);
          setShowModal(!showModal);
        }}
      >
        <div>{DENY_UPGRADE_TEXT}</div>
      </Popup>
    </Fragment>
  );
};

JsDatabase.propTypes = {
  isMobile: PropTypes.bool,
  auth: PropTypes.shape({ authenticated: PropTypes.bool, account_id: PropTypes.number }).isRequired,
  dispatch: PropTypes.func.isRequired,
};

JsDatabase.defaultProps = {
  isMobile: false,
};

export default JsDatabase;
